import { chakra, Portal } from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Icon, type LucideIcon } from '../icons/Icon';
import { Icons } from '../icons';
import { fuzzyScore } from './fuzzy';

export interface CommandItem {
  id: string;
  label: string;
  /** Section heading the item groups under (e.g. "Navigate", "Actions"). */
  group?: string;
  /** Extra search terms not shown in the label. */
  keywords?: string;
  icon?: LucideIcon;
  /** Right-aligned hint (e.g. a shortcut or path). */
  hint?: string;
  /** Run the command. The menu closes automatically afterwards. */
  perform: () => void;
}

const Backdrop = chakra('div', {
  base: {
    position: 'fixed',
    inset: 0,
    bg: 'overlay.backdrop',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    p: '4',
    pt: '15vh',
    zIndex: 'modal',
  },
});

const Panel = chakra('div', {
  base: {
    width: '100%',
    maxWidth: '40rem',
    bg: 'surface.raised',
    color: 'text.primary',
    borderRadius: 'xl',
    borderWidth: '1px',
    borderColor: 'border.default',
    boxShadow: 'lg',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxH: '70vh',
  },
});

const InputRow = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '2',
    px: '4',
    borderBottomWidth: '1px',
    borderColor: 'border.subtle',
  },
});

const Input = chakra('input', {
  base: {
    flex: '1',
    h: '13',
    bg: 'transparent',
    border: 'none',
    outline: 'none',
    fontFamily: 'body',
    fontSize: 'lg',
    color: 'text.primary',
    _placeholder: { color: 'text.muted' },
  },
});

const ListBox = chakra('ul', {
  base: { listStyle: 'none', m: 0, p: '2', overflowY: 'auto', flex: '1' },
});

const GroupLabel = chakra('li', {
  base: {
    px: '2',
    pt: '3',
    pb: '1',
    fontSize: '2xs',
    fontWeight: 'bold',
    letterSpacing: 'wider',
    textTransform: 'uppercase',
    color: 'text.muted',
  },
});

const Option = chakra('li', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '3',
    px: '2.5',
    py: '2.5',
    borderRadius: 'md',
    fontSize: 'md',
    color: 'text.primary',
    cursor: 'pointer',
    userSelect: 'none',
    '&[data-highlighted="true"]': { bg: 'action.primary/12', color: 'action.primary' },
  },
});

const Hint = chakra('span', {
  base: { ml: 'auto', fontFamily: 'mono', fontSize: 'xs', color: 'text.muted' },
});

const Empty = chakra('div', {
  base: { px: '4', py: '8', textAlign: 'center', color: 'text.muted', fontSize: 'md' },
});

export interface CommandMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: CommandItem[];
  placeholder?: string;
  /** Disable the global ⌘K / Ctrl+K binding (e.g. when another owner registers it). */
  disableHotkey?: boolean;
}

function searchText(c: CommandItem): string {
  return [c.label, c.group, c.keywords].filter(Boolean).join(' ');
}

/**
 * The ⌘K command palette (§ "no command surface" fix). Fuzzy-search across navigation
 * and key actions; open with ⌘K / Ctrl+K, arrow keys (or Tab / Shift+Tab) to move the
 * active option, Enter to run, Esc to close. Fully modal: focus stays on the combobox
 * input (activedescendant pattern), the backdrop dims the app, and focus returns to the
 * previously-focused element on close.
 */
export function CommandMenu({
  open,
  onOpenChange,
  commands,
  placeholder = 'Search or jump to…',
  disableHotkey = false,
}: CommandMenuProps) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  // Global hotkey.
  useEffect(() => {
    if (disableHotkey) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disableHotkey, onOpenChange]);

  // Ranked + grouped results.
  const results = useMemo(() => {
    const scored = commands
      .map((c) => ({ c, r: fuzzyScore(query, searchText(c)) }))
      .filter((x): x is { c: CommandItem; r: NonNullable<typeof x.r> } => x.r !== null)
      .sort((a, b) => b.r.score - a.r.score)
      .map((x) => x.c);
    return scored;
  }, [commands, query]);

  // Reset transient state on open; capture the element to restore focus to.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current = (document.activeElement as HTMLElement) ?? null;
      setQuery('');
      setHighlight(0);
      // Focus the input after paint.
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    // Restore focus to the opener when closing.
    restoreFocusRef.current?.focus?.();
  }, [open]);

  // Keep the highlighted index within bounds as results change.
  useEffect(() => {
    setHighlight((h) => (results.length === 0 ? 0 : Math.min(h, results.length - 1)));
  }, [results.length]);

  const runAt = useCallback(
    (index: number) => {
      const item = results[index];
      if (!item) return;
      close();
      item.perform();
    },
    [results, close],
  );

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (results.length === 0 ? 0 : (h + 1) % results.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (results.length === 0 ? 0 : (h - 1 + results.length) % results.length));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      runAt(highlight);
      return;
    }
    if (e.key === 'Tab') {
      // Modal palette: focus stays on the combobox input (activedescendant pattern), so
      // give Tab a visible effect instead of silently swallowing it — advance the active
      // option (Shift+Tab steps back), wrapping like the arrow keys. This keeps the user
      // from being trapped on an inert control with no feedback.
      e.preventDefault();
      setHighlight((h) => {
        if (results.length === 0) return 0;
        return e.shiftKey
          ? (h - 1 + results.length) % results.length
          : (h + 1) % results.length;
      });
    }
  };

  if (!open) return null;

  // Group results preserving best-score order of first appearance.
  const groups: { name: string; items: { item: CommandItem; index: number }[] }[] = [];
  results.forEach((item, index) => {
    const name = item.group ?? '';
    let g = groups.find((x) => x.name === name);
    if (!g) {
      g = { name, items: [] };
      groups.push(g);
    }
    g.items.push({ item, index });
  });

  return (
    <Portal>
      <Backdrop
        data-testid="command-menu-backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        <Panel
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Command menu"
          data-testid="command-menu"
          onKeyDown={onKeyDown}
        >
          <InputRow>
            <Icon as={Icons.Search} size="md" tone="muted" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              role="combobox"
              aria-expanded="true"
              aria-controls="command-menu-listbox"
              aria-activedescendant={
                results[highlight] ? `command-option-${results[highlight].id}` : undefined
              }
              aria-autocomplete="list"
              aria-label="Command menu search"
              data-testid="command-menu-input"
            />
          </InputRow>

          {results.length === 0 ? (
            <Empty data-testid="command-menu-empty">No matches for “{query}”.</Empty>
          ) : (
            <ListBox id="command-menu-listbox" role="listbox" aria-label="Commands">
              {groups.map((g) => (
                <chakra.li key={g.name || '_'} listStyleType="none">
                  {g.name && <GroupLabel as="div">{g.name}</GroupLabel>}
                  <chakra.ul role="none" listStyleType="none" m={0} p={0}>
                    {g.items.map(({ item, index }) => (
                      <Option
                        key={item.id}
                        id={`command-option-${item.id}`}
                        role="option"
                        aria-selected={index === highlight}
                        data-highlighted={index === highlight ? 'true' : undefined}
                        data-testid="command-menu-item"
                        onMouseMove={() => setHighlight(index)}
                        onClick={() => runAt(index)}
                      >
                        {item.icon && <Icon as={item.icon} size="md" tone="current" />}
                        <chakra.span>{item.label}</chakra.span>
                        {item.hint && <Hint>{item.hint}</Hint>}
                      </Option>
                    ))}
                  </chakra.ul>
                </chakra.li>
              ))}
            </ListBox>
          )}
        </Panel>
      </Backdrop>
    </Portal>
  );
}
